import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import DashboardLayout from '../../../components/dashboard/DashboardLayout';
import { Card, CardBody } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Table } from '../../../components/ui/Table';
import { Modal } from '../../../components/ui/Modal';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FiEye, FiUser, FiSearch, FiAlertCircle, FiPlus, FiX, FiFileText, FiMail, FiTrash2, FiActivity, FiClock, FiDollarSign } from 'react-icons/fi';
import { fetchBankClients, BankClientData, updateBankClient, migrationAddPinCodes } from '../../manage_clients/bank_clients/service/bankClientService';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';
import DeploymentModal from '../components/DeploymentModal';
import GuardDocumentsModal from '../components/GuardDocumentsModal';
import GuardTerminationModal from '../components/GuardTerminationModal';
import { clearAllDeployments } from '../service/deploymentService';
import { checkAllGuardsHaveDocuments, getGuardsWithDocuments, GuardWithDocuments } from '../service/documentService';
import { sendEmailViaFirestore } from '../service/emailService';
import { Select } from '../../../components/ui/Select';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  align-items: center;
  flex-wrap: wrap;
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing[4]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const StatCard = styled(Card)`
  text-align: center;
`;

const StatValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const SearchContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
  align-items: center;
  flex-wrap: nowrap;
`;

const SearchInputContainer = styled.div`
  flex: 1;
  min-width: 250px;
  display: flex;
  align-items: stretch;
`;

const WarningBanner = styled.div`
  background-color: ${theme.colors.accent.warning}20;
  border: 1px solid ${theme.colors.accent.warning};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[3]};
  margin-top: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.accent.warning};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing[8]};
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid ${theme.colors.light.border.light};
    border-top-color: ${theme.colors.primary[500]};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-top: ${theme.spacing[6]};
  flex-wrap: wrap;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const PaginationInfo = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[2]};
  border: none;
  border-radius: ${theme.borderRadius.md};
  background-color: ${theme.colors.primary[500]};
  color: ${theme.colors.white};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.typography.fontSize.sm};

  &:hover {
    background-color: ${theme.colors.primary[600]};
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GuardCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
`;

const GuardItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]};
  background: ${theme.colors.light.background.secondary};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.light.border.light};
  min-width: 150px;
`;

const GuardSlot = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]};
  background: ${theme.colors.light.background.tertiary};
  border-radius: ${theme.borderRadius.md};
  border: 1px dashed ${theme.colors.light.border.medium};
  min-width: 150px;
  color: ${theme.colors.light.text.muted};
  font-size: ${theme.typography.fontSize.sm};
`;



const StatusBadge = styled.span<{ variant?: 'complete' | 'incomplete' }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ variant }) =>
    variant === 'complete' ? theme.colors.accent.success + '20' : theme.colors.accent.warning + '20'
  };
  color: ${({ variant }) =>
    variant === 'complete' ? theme.colors.accent.success : theme.colors.accent.warning
  };
`;

interface DeploymentHistory {
  guard?: {
    id: string;
    name: string;
    guardId: string;
    cnic: string;
    image?: string;
  };
  postDate?: string | any;
  endDate?: string | any;
  status?: string;
  slotNumber?: number;
}

interface ClientWithDeployment extends BankClientData {
  currentDeployment?: DeploymentHistory;
  history?: DeploymentHistory[];
  requiredGuard?: number;
  deployedGuards?: Array<{
    id: string;
    name: string;
    guardId: string;
    cnic: string;
    image?: string;
  }>;
  hasIssue?: boolean;
  allGuardsHaveDocuments?: boolean;
  slotEndDates?: { [slotNumber: number]: any };
  otGuardCount?: number;
}

const PageContainer = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  @media(max-width: ${theme.breakpoints.md}) {
    overflow: visible;
    height: auto;
    min-height: auto;
    display: block;
  }
`;

// ... existing PageHeader ...

const MobileHeaderContainer = styled.div`
  display: none;
  @media(max-width: ${theme.breakpoints.md}) {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing[3]};
    position: sticky;
    top: 0;
    /* Full width fix matching BodyWrapper padding */
    width: calc(100% + ${theme.spacing[4]} * 2);
    margin-left: -${theme.spacing[4]};
    margin-right: -${theme.spacing[4]};
    background-color: ${theme.colors.light.background.primary};
    z-index: 40;
    padding: ${theme.spacing[4]};
    margin-bottom: ${theme.spacing[4]};
    border-bottom: 1px solid ${theme.colors.light.border.light};
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  }
`;

const MobileActionRow = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  width: 100%;
  align-items: center;
`;

const MobileCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  padding-bottom: ${theme.spacing[4]};
`;

const MobileCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  padding: ${theme.spacing[2]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  cursor: pointer;
  transition: background-color 0.2s;
  min-height: 80px;

  &:active {
    background-color: ${theme.colors.light.background.secondary};
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const MobileCardImage = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${theme.colors.primary[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.primary[600]};
  font-weight: bold;
  flex-shrink: 0;
  border: 2px solid ${theme.colors.primary[100]};
`;

const MobileCardHeaderContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MobileCardTitle = styled.div`
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  white-space: normal;
`;

const MobileCardSubtitle = styled.div`
  color: ${theme.colors.light.text.secondary};
  font-size: ${theme.typography.fontSize.xs};
`;

const MobileCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  padding-left: calc(48px + ${theme.spacing[3]}); /* Align with content */
`;

const MobileCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${theme.typography.fontSize.xs};
`;

const MobileStatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
`;

const MobileStatCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[2]};
  text-align: center;
`;

const MobileStatValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
`;

const MobileStatLabel = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
`;

// Helper for initial icon
const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
};

const ModalSection = styled.div`
  padding: ${theme.spacing[4]};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.md};
  background-color: ${theme.colors.white};
`;

const ChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.light.background.secondary};
  min-height: 42px;
  align-items: center;
`;

const Chip = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: 2px ${theme.spacing[2]};
  background: ${theme.colors.primary[500]};
  color: white;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};

  svg {
    cursor: pointer;
    &:hover {
      color: ${theme.colors.accent.danger};
    }
  }
`;

const AttachmentList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${theme.spacing[3]};
  margin-top: ${theme.spacing[2]};
  max-height: 200px;
  overflow-y: auto;
  padding: ${theme.spacing[2]};
`;

const AttachmentItem = styled.div<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  border: 1px solid ${({ active }) => active ? theme.colors.primary[500] : theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.md};
  background: ${({ active }) => active ? theme.colors.primary[50] : theme.colors.white};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
    background: ${theme.colors.primary[50]};
  }
`;

const EmailBody = styled.textarea`
  width: 100%;
  min-height: 300px;
  padding: ${theme.spacing[4]};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.white};
  font-family: 'Inter', sans-serif;
  font-size: ${theme.typography.fontSize.sm};
  line-height: 1.6;
  resize: vertical;
  color: ${theme.colors.light.text.primary};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 2px ${theme.colors.primary[500]}20;
  }
`;

// Helper to format date safely
const formatDeploymentDate = (dateVal: any) => {
  if (!dateVal) return '—';
  try {
    let date: Date;
    if (dateVal instanceof Date) {
      date = dateVal;
    } else if (typeof dateVal.toDate === 'function') {
      date = dateVal.toDate();
    } else if (dateVal.seconds) {
      date = new Date(dateVal.seconds * 1000);
    } else {
      date = new Date(dateVal);
    }

    if (isNaN(date.getTime())) return '—';

    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '—';
  }
};

const HistoryCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[3]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  box-shadow: ${theme.shadows.sm};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: ${theme.shadows.md};
    border-color: ${theme.colors.primary[200]};
  }
`;

const HistoryCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${theme.colors.light.background.tertiary};
  padding-bottom: ${theme.spacing[2]};
`;

const HistoryCardBody = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${theme.spacing[4]};
  align-items: center;
`;

const HistoryCardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${theme.spacing[2]};
  border-top: 1px solid ${theme.colors.light.background.tertiary};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
`;

const GuardAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${theme.colors.light.background.tertiary};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 2px solid ${theme.colors.primary[100]};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DateBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  color: ${theme.colors.light.text.primary};
  font-weight: ${theme.typography.fontWeight.medium};
  font-size: ${theme.typography.fontSize.sm};
  background: ${theme.colors.light.background.secondary};
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.md};
`;

const GuardImage = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid ${theme.colors.light.border.medium};
`;

const GuardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const GuardName = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.primary};
`;

const GuardCNIC = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[8]};
  color: ${theme.colors.light.text.secondary};
`;

const EmptyStateText = styled.p`
  font-size: ${theme.typography.fontSize.base};
  margin: 0;
`;

const DetailGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
  margin-bottom: ${theme.spacing[4]};
`;

const DetailLabel = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.secondary};
`;

const DetailValue = styled.span`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.primary};
`;

const Badge = styled.span<{ variant?: 'active' | 'completed' }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ variant }) =>
    variant === 'active' ? theme.colors.accent.success : theme.colors.light.background.tertiary
  };
  color: ${({ variant }) =>
    variant === 'active' ? theme.colors.white : theme.colors.light.text.secondary
  };
`;


const RequiredGuardsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  client: ClientWithDeployment | null;
  onUpdate: (requiredGuard: number, otGuardCount: number) => Promise<void>;
  loading: boolean;
}> = ({ isOpen, onClose, client, onUpdate, loading }) => {
  const [requiredGuard, setRequiredGuard] = useState(3);
  const [otGuardCount, setOtGuardCount] = useState(0);

  useEffect(() => {
    if (client && isOpen) {
      setRequiredGuard(client.requiredGuard || 3);
      setOtGuardCount(client.otGuardCount || 0);
    }
  }, [client, isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Guard Slots"
      footer={
        <div style={{ display: 'flex', gap: theme.spacing[2], justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onUpdate(requiredGuard, otGuardCount)} loading={loading}>Save Changes</Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
        <div style={{ marginBottom: theme.spacing[4] }}>
          <label style={{ display: 'block', marginBottom: theme.spacing[2], fontWeight: 'bold', color: theme.colors.light.text.primary }}>
            Required Guards (Permanent)
          </label>
          <Input
            type="number"
            min={1}
            max={20}
            value={String(requiredGuard)}
            onChange={(e) => setRequiredGuard(parseInt(e.target.value) || 1)}
            fullWidth
            dark
          />
          <p style={{ fontSize: '12px', color: theme.colors.light.text.secondary, marginTop: '4px' }}>
            Number of permanent guard slots for this branch.
          </p>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: theme.spacing[2], fontWeight: 'bold', color: theme.colors.light.text.primary }}>
            Overtime (OT) Guard Slots
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[3] }}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setOtGuardCount(Math.max(0, otGuardCount - 1))}
              style={{ width: '40px', height: '40px', fontSize: '20px', padding: 0 }}
            >
              -
            </Button>
            <span style={{ minWidth: '40px', textAlign: 'center', fontWeight: 'bold', fontSize: '20px', color: theme.colors.primary[600] }}>
              {otGuardCount}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setOtGuardCount(otGuardCount + 1)}
              style={{ width: '40px', height: '40px', fontSize: '20px', padding: 0 }}
            >
              +
            </Button>
          </div>
          <p style={{ fontSize: '12px', color: theme.colors.light.text.secondary, marginTop: '4px' }}>
            Extra temporary slots for overtime guards.
          </p>
        </div>
      </div>
    </Modal>
  );
};

const BankDeploymentPage: React.FC = () => {
  const { user, companyData, staffData } = useAuth();
  const [clients, setClients] = useState<ClientWithDeployment[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<ClientWithDeployment | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showDeploymentModal, setShowDeploymentModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedClientForDocuments, setSelectedClientForDocuments] = useState<ClientWithDeployment | null>(null);
  const [showClearConfirmModal, setShowClearConfirmModal] = useState(false);
  const [clearingDeployments, setClearingDeployments] = useState(false);

  const [showTerminationModal, setShowTerminationModal] = useState(false);
  const [terminationGuard, setTerminationGuard] = useState<any>(null);
  const [terminationClientPath, setTerminationClientPath] = useState<string>('');

  // Required Guards Management state
  const [showRequiredGuardsModal, setShowRequiredGuardsModal] = useState(false);
  const [selectedClientForRequiredGuards, setSelectedClientForRequiredGuards] = useState<ClientWithDeployment | null>(null);
  const [updatingRequiredGuards, setUpdatingRequiredGuards] = useState(false);
  const [initialDeploymentData, setInitialDeploymentData] = useState<{ path?: string; slot?: number; date?: Date } | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  const [selectedBank, setSelectedBank] = useState<string>('');
  const [migratingPins, setMigratingPins] = useState(false);

  // Email state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [newRecipient, setNewRecipient] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [selectedAttachments, setSelectedAttachments] = useState<string[]>([]);
  const [availableDocuments, setAvailableDocuments] = useState<any[]>([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [isBodyLoaded, setIsBodyLoaded] = useState(false);

  // Salary state
  const [showSalaryModal, setShowSalaryModal] = useState(false);

  // Use company ID if available, otherwise fall back to user UID (same as service)
  const companyId = companyData?.id || auth.currentUser?.uid || '';

  // Check if user is a company owner/admin (not just staff)
  const userEmail = user?.email?.toLowerCase();
  const isCompanyOwner = !!companyData && (
    !staffData || // No staffData = pure company owner
    (userEmail && (
      companyData.email?.toLowerCase() === userEmail ||
      companyData.emails?.some((email: string) => email.toLowerCase() === userEmail)
    )) // Email matches company = company owner
  );

  // Check deployment permissions
  const canViewDeployment = isCompanyOwner || (staffData?.access?.deployment === true);
  const canCreateDeployment = isCompanyOwner || (
    staffData?.access?.deployment === true &&
    (staffData?.permissions?.deployment === 'edit' || staffData?.permissions?.deployment === 'add' || staffData?.permissions?.deployment === 'delete')
  );

  // Check client management permissions
  const canManageClients = isCompanyOwner || (
    staffData?.access?.clients === true &&
    (staffData?.permissions?.clients === 'edit' || staffData?.permissions?.clients === 'add' || staffData?.permissions?.clients === 'delete')
  );

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, companyData]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Ensure we have a valid company ID (same logic as service)
      const validCompanyId = companyData?.id || auth.currentUser?.uid;
      if (!validCompanyId) {
        throw new Error('Company ID or user authentication required');
      }

      const clientsData = await fetchBankClients(validCompanyId);

      // Fetch deployment data for each client
      const clientsWithDeployment = await Promise.all(
        clientsData.map(async (client) => {
          const clientRef = doc(db, 'bankClient', validCompanyId, 'banks', client.bankId, 'branches', client.branchId);
          const clientSnap = await getDoc(clientRef);
          const clientData = clientSnap.data();

          const requiredGuard = clientData?.requiredGuard || 3;
          const currentDeployment = clientData?.currentDeployment || null;
          const history = clientData?.history || [];
          const salary = clientData?.salary || 0;
          const salaryHistory = clientData?.salaryHistory || [];
          const locationName = clientData?.locationName || '';

          // Extract deployed guards from currentDeployment - normalize to array
          const guard = currentDeployment?.guard || null;
          const deployedGuards = Array.isArray(guard) ? guard : guard ? [guard] : [];

          // Check if client has deployment issue (missing guards)
          const hasIssue = deployedGuards.length < requiredGuard;

          // Check if all guards have documents
          const allGuardsHaveDocuments = true; // Disabled validation as per user request
          /*
          if (deployedGuards.length > 0) {
            try {
              allGuardsHaveDocuments = await checkAllGuardsHaveDocuments(`bankClient/${validCompanyId}/banks/${client.bankId}/branches/${client.branchId}`);
            } catch (error) {
              console.error('Error checking documents:', error);
            }
          }
          */

          return {
            ...client,
            requiredGuard,
            currentDeployment,
            history,
            deployedGuards,
            hasIssue,
            allGuardsHaveDocuments,
            slotEndDates: clientData?.slotEndDates || {},
            salary,
            salaryHistory,
            locationName,
            otGuardCount: client.otGuardCount || 0,
          } as ClientWithDeployment;
        })
      );

      setClients(clientsWithDeployment);
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast.error(error.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [companyData]);

  const filteredClients = useMemo(() => {
    let filtered = clients;

    if (selectedBank) {
      filtered = filtered.filter((c) => c.bankName === selectedBank);
    }

    if (!searchTerm.trim()) return filtered;

    const term = searchTerm.toLowerCase();
    return filtered.filter((c) => {
      const bankName = c.bankName?.toLowerCase() || '';
      const branchCode = c.branchCode ? String(c.branchCode).toLowerCase() : '';
      const address = c.address?.toLowerCase() || '';
      const locationName = c.locationName?.toLowerCase() || '';
      return bankName.includes(term) || branchCode.includes(term) || address.includes(term) || locationName.includes(term);
    });
  }, [clients, searchTerm, selectedBank]);

  const loadAvailableDocuments = async (client: ClientWithDeployment) => {
    try {
      const path = `bankClient/${companyId}/banks/${client.bankId}/branches/${client.branchId}`;
      const guardsWithDocs = await getGuardsWithDocuments(path);
      const docs: any[] = [];
      guardsWithDocs.forEach(g => {
        (g.documents || []).forEach(d => {
          docs.push({
            ...d,
            guardName: g.name,
            guardId: g.guardId
          });
        });
      });
      setAvailableDocuments(docs);
    } catch (err) {
      console.error('Error loading documents:', err);
    }
  };

  const generateEmailBody = (client: ClientWithDeployment) => {
    const guardsList = client.deployedGuards?.map(g => `- ${g.name} (${g.guardId})`).join('\n') || 'No guards deployed';

    // Get doc names from selected URLs
    const selectedDocNames = selectedAttachments.map(url => {
      const doc = availableDocuments.find(d => d.url === url);
      return doc ? `${doc.name} (${doc.guardName})` : '';
    }).filter(name => name !== '');

    const attachmentsSection = selectedDocNames.length > 0
      ? `\nAttached Documents: \n${selectedDocNames.map(name => `- ${name}`).join('\n')} \n`
      : '';

    const body = `Dear Sir / Madam,

  Please find the deployment details for ${client.bankName} - ${client.branchCode}.

Deployment Details:
${guardsList}

Branch Pin Code: ${client.pinCode || 'Pending'}
${attachmentsSection}
If you have any questions, please feel free to contact us.

  Regards,
  ${companyData?.name || 'Security Team'} `;

    setEmailBody(body);
    setIsBodyLoaded(true);
  };

  // Mobile responsiveness
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [visibleItems, setVisibleItems] = useState(15);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Infinite scroll logic (client-side slicing)
  useEffect(() => {
    if (!isMobile) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && visibleItems < filteredClients.length) {
          setVisibleItems((prev) => Math.min(prev + 15, filteredClients.length));
        }
      },
      { threshold: 0.5 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [isMobile, filteredClients.length, visibleItems]);

  // Unique banks for dropdown
  const uniqueBanks = useMemo(() => {
    const banksSet = new Set<string>();
    clients.forEach((c) => {
      if (c.bankName) banksSet.add(c.bankName);
    });
    return Array.from(banksSet).sort();
  }, [clients]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
    setVisibleItems(15); // Reset visible items for mobile too
  }, [searchTerm, selectedBank]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalClients = filteredClients.length;
    const totalGuardsDeployed = filteredClients.reduce((sum, c) => sum + (c.deployedGuards?.length || 0), 0);
    const clientsWithIssues = filteredClients.filter((c) => c.hasIssue).length;
    const totalRequiredGuards = filteredClients.reduce((sum, c) => sum + (c.requiredGuard || 3), 0);

    return {
      totalClients,
      totalGuardsDeployed,
      clientsWithIssues,
      totalRequiredGuards,
    };
  }, [filteredClients]);

  const handleViewHistory = (client: ClientWithDeployment) => {
    setSelectedClient(client);
    setShowHistoryModal(true);
  };

  const handleSlotClick = (client: ClientWithDeployment, slotIndex: number, guard: any | null) => {
    if (!canCreateDeployment) return;

    const clientPath = `bankClient/${companyId}/banks/${client.bankId}/branches/${client.branchId}`;

    if (guard) {
      // If guard exists, open termination modal
      setTerminationGuard(guard);
      setTerminationClientPath(clientPath);
      setInitialDeploymentData({ path: clientPath, slot: slotIndex });
      setShowTerminationModal(true);
    } else {
      // If empty slot, open deployment modal with pre-selection
      let suggestedDate = new Date();
      const slotEndDates = (client as any).slotEndDates;
      if (slotEndDates && slotEndDates[slotIndex + 1]) {
        const lastDate = slotEndDates[slotIndex + 1];
        suggestedDate = new Date(lastDate.seconds ? lastDate.seconds * 1000 : lastDate);
        suggestedDate.setDate(suggestedDate.getDate() + 1);
      }

      setInitialDeploymentData({
        path: clientPath,
        slot: slotIndex,
        date: suggestedDate
      });
      setShowDeploymentModal(true);
    }
  };

  const handleTerminated = (endDate: Date) => {
    // After termination, suggest next day for deployment
    const nextDate = new Date(endDate);
    nextDate.setDate(nextDate.getDate() + 1);

    setInitialDeploymentData(prev => ({
      ...prev,
      date: nextDate
    }));

    // Refresh data
    loadData();

    // Open deployment modal immediately
    setShowDeploymentModal(true);
  };

  const handleUpdateRequiredGuards = async (requiredGuard: number, otGuardCount: number) => {
    if (!selectedClientForRequiredGuards || !companyId) return;

    setUpdatingRequiredGuards(true);
    try {
      await updateBankClient(
        selectedClientForRequiredGuards.branchId,
        selectedClientForRequiredGuards.bankId,
        { requiredGuard, otGuardCount },
        companyId as string
      );
      toast.success('Guard slots updated successfully');
      setShowRequiredGuardsModal(false);
      loadData();
    } catch (error: any) {
      console.error('Error updating guard slots:', error);
      toast.error(error.message || 'Failed to update guard slots');
    } finally {
      setUpdatingRequiredGuards(false);
    }
  };

  // Render guards - show all required guard slots
  const renderGuards = (client: ClientWithDeployment) => {
    const requiredGuard = client.requiredGuard || 3;
    const otGuardCount = client.otGuardCount || 0;
    const deployedGuards = client.deployedGuards || [];
    const guards: React.ReactNode[] = [];

    // Permanent Slots
    for (let i = 0; i < requiredGuard; i++) {
      const slotNumber = i + 1;
      const slotIndex = i;

      // Find guard assigned to this specific slot number
      const guard = deployedGuards.find((g: any) => g.slotNumber === slotNumber);

      if (guard) {
        guards.push(
          <GuardItem
            key={`perm-${i}`}
            onClick={() => handleSlotClick(client, slotIndex, guard)}
            style={{ cursor: canCreateDeployment ? 'pointer' : 'default' }}
          >
            {guard.image ? (
              <GuardImage
                src={guard.image}
                alt={guard.name}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            ) : (
              <FiUser size={20} style={{ color: theme.colors.light.text.muted }} />
            )}
            <GuardInfo>
              <GuardName>{guard.name || guard.guardId || '—'}</GuardName>
              <GuardCNIC>{guard.cnic || '—'}</GuardCNIC>
            </GuardInfo>
          </GuardItem>
        );
      } else {
        guards.push(
          <GuardSlot
            key={`perm-${i}`}
            onClick={() => handleSlotClick(client, slotIndex, null)}
            style={{ cursor: canCreateDeployment ? 'pointer' : 'default' }}
          >
            <FiUser size={20} style={{ color: theme.colors.light.text.muted }} />
            <span>Empty Slot</span>
          </GuardSlot>
        );
      }
    }

    // OT Slots
    for (let i = 0; i < otGuardCount; i++) {
      const slotNumber = 101 + i;
      const slotIndex = slotNumber - 1;
      const guard = deployedGuards.find((g: any) => g.slotNumber === slotNumber);

      if (guard) {
        guards.push(
          <GuardItem
            key={`ot-${i}`}
            onClick={() => handleSlotClick(client, slotIndex, guard)}
            style={{ cursor: canCreateDeployment ? 'pointer' : 'default', borderStyle: 'solid', borderColor: theme.colors.accent.info }}
          >
            {guard.image ? (
              <GuardImage
                src={guard.image}
                alt={guard.name}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            ) : (
              <FiUser size={20} style={{ color: theme.colors.light.text.muted }} />
            )}
            <GuardInfo>
              <GuardName>{guard.name || guard.guardId || '—'} <span style={{ color: theme.colors.accent.info, fontSize: '10px' }}>(OT)</span></GuardName>
              <GuardCNIC>{guard.cnic || '—'}</GuardCNIC>
            </GuardInfo>
          </GuardItem>
        );
      } else {
        guards.push(
          <GuardSlot
            key={`ot-${i}`}
            onClick={() => handleSlotClick(client, slotIndex, null)}
            style={{ cursor: canCreateDeployment ? 'pointer' : 'default', borderStyle: 'dashed', borderColor: theme.colors.accent.info }}
          >
            <FiUser size={20} style={{ color: theme.colors.accent.info }} />
            <span style={{ color: theme.colors.accent.info }}>OT Empty Slot</span>
          </GuardSlot>
        );
      }
    }

    return <GuardCell>{guards}</GuardCell>;
  };

  const clientsWithIssues = filteredClients.filter((c) => c.hasIssue);
  const clientsWithIssuesCount = clientsWithIssues.length;

  // Pagination
  const paginatedClients = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredClients.slice(startIndex, endIndex);
  }, [filteredClients, currentPage]);

  const totalPages = Math.ceil(filteredClients.length / pageSize);

  // Prepare clients data for the modal - use full clients list, not filtered
  const deploymentClients = useMemo(() => {
    return clients.map((c) => ({
      id: c.branchId,
      name: c.bankName || '',
      type: 'Bank' as const,
      address: c.address,
      bankName: c.bankName,
      branchCode: c.branchCode,
      path: `bankClient/${companyId}/banks/${c.bankId}/branches/${c.branchId}`,
      deployedGuards: c.deployedGuards,
      requiredGuard: c.requiredGuard,
    }));
  }, [clients, companyId]);

  // Show permission denied message if user doesn't have view access
  if (!canViewDeployment) {
    return (
      <DashboardLayout>
        <div style={{
          textAlign: 'center',
          padding: theme.spacing[12],
          color: theme.colors.light.text.secondary
        }}>
          <h2>Access Denied</h2>
          <p>You don't have permission to view deployment pages.</p>
        </div>
      </DashboardLayout>
    );
  }

  // Mobile Render Logic
  if (isMobile) {
    return (
      <DashboardLayout>
        <PageContainer>
          <MobileHeaderContainer>
            <PageTitle>Bank Deployment</PageTitle>
            <SearchInputContainer>
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                dark
                fullWidth
              />
            </SearchInputContainer>
            <MobileActionRow>
              <div style={{ flex: 1 }}>
                <Select
                  value={selectedBank}
                  onChange={(e) => setSelectedBank(e.target.value)}
                  dark
                  fullWidth
                >
                  <option value="">All Banks</option>
                  {uniqueBanks.map((bank) => (
                    <option key={bank} value={bank}>
                      {bank}
                    </option>
                  ))}
                </Select>
              </div>
              {canViewDeployment && canCreateDeployment && (
                <Button onClick={() => setShowDeploymentModal(true)} style={{ width: 'auto' }}>
                  <FiPlus size={18} />
                </Button>
              )}
            </MobileActionRow>
          </MobileHeaderContainer>

          <MobileStatGrid>
            <MobileStatCard>
              <MobileStatValue>{summaryStats.totalClients}</MobileStatValue>
              <MobileStatLabel>Total Clients</MobileStatLabel>
            </MobileStatCard>
            <MobileStatCard>
              <MobileStatValue color={theme.colors.primary[600]}>{summaryStats.totalGuardsDeployed}</MobileStatValue>
              <MobileStatLabel>Deployed</MobileStatLabel>
            </MobileStatCard>
            <MobileStatCard>
              <MobileStatValue color={theme.colors.accent.warning}>{summaryStats.clientsWithIssues}</MobileStatValue>
              <MobileStatLabel>Issues</MobileStatLabel>
            </MobileStatCard>
            <MobileStatCard>
              <MobileStatValue color={theme.colors.accent.info}>{summaryStats.totalRequiredGuards}</MobileStatValue>
              <MobileStatLabel>Required</MobileStatLabel>
            </MobileStatCard>
          </MobileStatGrid>

          {clientsWithIssuesCount > 0 && (
            <div style={{ padding: `0 ${theme.spacing[1]} `, marginBottom: theme.spacing[3] }}>
              <WarningBanner style={{ marginTop: 0 }}>
                <FiAlertCircle size={20} />
                <span>{clientsWithIssuesCount} Issues</span>
              </WarningBanner>
            </div>
          )}

          <MobileCardContainer>
            {filteredClients.slice(0, visibleItems).map((client) => (
              <MobileCard key={client.branchId} onClick={() => handleViewHistory(client)}>
                <MobileCardHeader>
                  <MobileCardImage>
                    {client.bankName ? getInitials(client.bankName) : <FiUser />}
                  </MobileCardImage>
                  <MobileCardHeaderContent>
                    <MobileCardTitle>{client.bankName} - {client.branchCode}</MobileCardTitle>
                    <MobileCardSubtitle>{client.address}</MobileCardSubtitle>
                  </MobileCardHeaderContent>
                  <StatusBadge variant={client.hasIssue || !client.allGuardsHaveDocuments ? 'incomplete' : 'complete'}>
                    {client.hasIssue ? 'Issue' : 'OK'}
                  </StatusBadge>
                </MobileCardHeader>

                {/* Additional Mobile Card Rows */}
                <MobileCardRow>
                  <span style={{ color: theme.colors.light.text.secondary }}>Salary:</span>
                  <span style={{ color: theme.colors.accent.success, fontWeight: 'bold' }}>Rs. {client.salary?.toLocaleString() || '0'}</span>
                </MobileCardRow>

                {/* Deployed Guards Section */}
                <div style={{ marginTop: theme.spacing[2], paddingTop: theme.spacing[2], borderTop: `1px dashed ${theme.colors.light.border.medium} ` }}>
                  <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.secondary, marginBottom: theme.spacing[2] }}>
                    Deployed Guards ({client.deployedGuards?.length || 0}/{client.requiredGuard || 3})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[2] }}>
                    {(() => {
                      const required = client.requiredGuard || 3;
                      const deployed = client.deployedGuards || [];
                      const items = [];
                      for (let i = 0; i < required; i++) {
                        const slotNumber = i + 1;
                        const slotIndex = i;

                        // Find guard assigned to this specific slot number
                        const g = deployed.find((guard: any) => guard.slotNumber === slotNumber);

                        if (g) {
                          items.push(
                            <div
                              key={i}
                              style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2], cursor: canCreateDeployment ? 'pointer' : 'default' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSlotClick(client, slotIndex, g);
                              }}
                            >
                              {g.image ? (
                                <img src={g.image} alt={g.name} style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />
                              ) : (
                                <div style={{ width: 24, height: 24, borderRadius: '50%', background: theme.colors.light.background.tertiary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <FiUser size={12} color={theme.colors.light.text.muted} />
                                </div>
                              )}
                              <span style={{ fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.bold, color: '#000000' }}>
                                {g.name} <span style={{ fontWeight: 'normal', color: theme.colors.light.text.secondary }}>({g.guardId})</span>
                              </span>
                            </div>
                          );
                        } else {
                          items.push(
                            <div
                              key={i}
                              style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2], opacity: 0.5, cursor: canCreateDeployment ? 'pointer' : 'default' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSlotClick(client, slotIndex, null);
                              }}
                            >
                              <div style={{ width: 24, height: 24, borderRadius: '50%', border: `1px dashed ${theme.colors.light.border.medium} `, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FiUser size={12} color={theme.colors.light.text.muted} />
                              </div>
                              <span style={{ fontSize: theme.typography.fontSize.xs, fontStyle: 'italic', color: theme.colors.light.text.muted }}>
                                Empty Slot
                              </span>
                            </div>
                          );
                        }
                      }
                      return items;
                    })()}
                  </div>
                </div>

              </MobileCard>
            ))}
            {visibleItems < filteredClients.length && (
              <div ref={loadMoreRef} style={{ padding: '20px', textAlign: 'center' }}>
                <span style={{ color: theme.colors.light.text.secondary }}>Loading more...</span>
              </div>
            )}
            {filteredClients.length === 0 && !loading && (
              <EmptyState>
                <EmptyStateText>No clients found.</EmptyStateText>
              </EmptyState>
            )}
          </MobileCardContainer>

        </PageContainer>
        {/* Modals */}
        <Modal
          isOpen={showHistoryModal}
          onClose={() => {
            setShowHistoryModal(false);
            setSelectedClient(null);
          }}
          title={`Deployment History`}
          size="lg"
          footer={
            <Button variant="secondary" onClick={() => {
              setShowHistoryModal(false);
              setSelectedClient(null);
            }}>
              Close
            </Button>
          }
        >
          {selectedClient && (
            <>
              {selectedClient.currentDeployment && (
                <DetailGroup>
                  <DetailLabel>Current Deployment</DetailLabel>
                  <div style={{ padding: theme.spacing[3], background: theme.colors.light.background.secondary, borderRadius: theme.borderRadius.md }}>
                    {/* Reuse existing modal content logic or simplify for mobile if needed, keeping same for now */}
                    {selectedClient.currentDeployment.guard && (
                      <>
                        <DetailValue>
                          <strong>Guard:</strong> {selectedClient.currentDeployment.guard.name || selectedClient.currentDeployment.guard.guardId || '—'}
                        </DetailValue>
                        <DetailValue>
                          <strong>CNIC:</strong> {selectedClient.currentDeployment.guard.cnic || '—'}
                        </DetailValue>
                      </>
                    )}
                    <DetailValue>
                      <strong>Status:</strong> {selectedClient.currentDeployment.status}
                    </DetailValue>
                  </div>
                </DetailGroup>
              )}
              {/* Render guards list for checking documents etc */}
              <DetailGroup>
                <DetailLabel>Deployed Guards</DetailLabel>
                {selectedClient.deployedGuards && selectedClient.deployedGuards.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[2] }}>
                    {selectedClient.deployedGuards.map((g, idx) => (
                      <div key={idx} style={{ padding: theme.spacing[2], border: `1px solid ${theme.colors.light.border.light} `, borderRadius: theme.borderRadius.md }}>
                        <div><strong>{g.name}</strong> ({g.guardId})</div>
                        <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>{g.cnic}</div>
                      </div>
                    ))}
                  </div>
                ) : <div>No guards deployed</div>}
              </DetailGroup>
            </>
          )}
        </Modal>

        {/* ... Keep other modals (Deployment, Documents, Clear) instantiated here as well ... */}
        {showDeploymentModal && (
          <DeploymentModal
            isOpen={showDeploymentModal}
            onClose={() => setShowDeploymentModal(false)}
            clients={deploymentClients}
            clientType="Bank"
            onDeploymentSaved={() => {
              loadData();
              setShowDeploymentModal(false);
            }}
          />
        )}

        {showDocumentsModal && selectedClientForDocuments && (
          <GuardDocumentsModal
            isOpen={showDocumentsModal}
            onClose={() => {
              setShowDocumentsModal(false);
              setSelectedClientForDocuments(null);
            }}
            clientPath={`Bank / ${companyId} /banks/${selectedClientForDocuments.bankId} /branches/${selectedClientForDocuments.branchId} `}
            clientName={`${selectedClientForDocuments.bankName} - ${selectedClientForDocuments.branchCode} `}
          />
        )}

        <Modal
          isOpen={showClearConfirmModal}
          onClose={() => !clearingDeployments && setShowClearConfirmModal(false)}
          title="Clear All Deployments"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setShowClearConfirmModal(false)}
                disabled={clearingDeployments}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  setClearingDeployments(true);
                  try {
                    await clearAllDeployments(companyId);
                    toast.success('All deployments cleared successfully');
                    setShowClearConfirmModal(false);
                    loadData();
                  } catch (error: any) {
                    console.error('Error clearing deployments:', error);
                    toast.error(error.message || 'Failed to clear deployments');
                  } finally {
                    setClearingDeployments(false);
                  }
                }}
                disabled={clearingDeployments}
                loading={clearingDeployments}
              >
                Clear All
              </Button>
            </>
          }
        >
          <div style={{ padding: theme.spacing[4], textAlign: 'center' }}>
            <FiAlertCircle size={48} color={theme.colors.accent.danger} style={{ marginBottom: theme.spacing[3] }} />
            <p style={{ fontSize: theme.typography.fontSize.lg, fontWeight: 'bold', marginBottom: theme.spacing[2] }}>
              Are you sure?
            </p>
            <p style={{ color: theme.colors.light.text.secondary }}>
              This will remove ALL guard deployments from ALL branches. This action cannot be undone.
            </p>
          </div>
        </Modal>

      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Bank Deployment</PageTitle>
        <HeaderActions>
          {canViewDeployment && canCreateDeployment && (
            <div style={{ display: 'flex', gap: theme.spacing[2] }}>
              {isCompanyOwner && (
                <Button
                  variant="secondary"
                  onClick={async () => {
                    if (window.confirm('Generate pin codes for all branches missing them?')) {
                      setMigratingPins(true);
                      try {
                        await migrationAddPinCodes(companyId);
                        toast.success('Pin codes generated successfully');
                        loadData();
                      } catch (err: any) {
                        toast.error(err.message);
                      } finally {
                        setMigratingPins(false);
                      }
                    }
                  }}
                  disabled={migratingPins}
                >
                  <FiActivity size={18} style={{ marginRight: theme.spacing[2] }} />
                  {migratingPins ? 'Migrating...' : 'Run Pin Migration'}
                </Button>
              )}
              <Button onClick={() => setShowDeploymentModal(true)}>
                <FiPlus size={18} style={{ marginRight: theme.spacing[2] }} />
                Deploy Guards
              </Button>
            </div>
          )}
        </HeaderActions>
      </PageHeader>


      {/* Summary Cards */}
      <StatsContainer>
        <StatCard>
          <CardBody>
            <StatValue>{summaryStats.totalClients}</StatValue>
            <StatLabel>Total Clients</StatLabel>
          </CardBody>
        </StatCard>
        <StatCard>
          <CardBody>
            <StatValue color={theme.colors.primary[600]}>{summaryStats.totalGuardsDeployed}</StatValue>
            <StatLabel>Guards Deployed</StatLabel>
          </CardBody>
        </StatCard>
        <StatCard>
          <CardBody>
            <StatValue color={theme.colors.accent.warning}>{summaryStats.clientsWithIssues}</StatValue>
            <StatLabel>Clients with Issues</StatLabel>
          </CardBody>
        </StatCard>
        <StatCard>
          <CardBody>
            <StatValue color={theme.colors.accent.info}>{summaryStats.totalRequiredGuards}</StatValue>
            <StatLabel>Total Required Guards</StatLabel>
          </CardBody>
        </StatCard>
      </StatsContainer>

      <Card>
        <CardBody>
          <SearchContainer>
            <div style={{ flex: '0 0 15%', minWidth: '150px' }}>
              <Select
                value={selectedBank}
                onChange={(e) => setSelectedBank(e.target.value)}
                dark
                fullWidth
              >
                <option value="">All Banks</option>
                {uniqueBanks.map((bank) => (
                  <option key={bank} value={bank}>
                    {bank}
                  </option>
                ))}
              </Select>
            </div>
            <SearchInputContainer>
              <Input
                placeholder="Search by bank name, branch code, or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                dark
                fullWidth
              />
            </SearchInputContainer>
            {(searchTerm || selectedBank) && (
              <Button variant="secondary" onClick={() => { setSearchTerm(''); setSelectedBank(''); }}>
                Clear
              </Button>
            )}
          </SearchContainer>

          {clientsWithIssuesCount > 0 && (
            <WarningBanner>
              <FiAlertCircle size={20} />
              <span>
                {clientsWithIssuesCount} client{clientsWithIssuesCount > 1 ? 's have' : ' has'} guard deployment issues.
                Please review and deploy additional guards as needed.
              </span>
            </WarningBanner>
          )}

          {!loading && filteredClients.length === 0 && searchTerm && (
            <EmptyState>
              <EmptyStateText>No clients found matching your search.</EmptyStateText>
            </EmptyState>
          )}

          {loading ? (
            <Loader />
          ) : filteredClients.length > 0 ? (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Client Details</Table.HeaderCell>
                  <Table.HeaderCell>Required Guards</Table.HeaderCell>
                  <Table.HeaderCell>Deployed Guards</Table.HeaderCell>
                  <Table.HeaderCell>Salary (Rs.)</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {paginatedClients.map((client) => (
                  <Table.Row key={client.branchId}>
                    <Table.Cell>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <div style={{ fontWeight: theme.typography.fontWeight.bold, color: theme.colors.light.text.primary }}>
                          {client.bankName || '—'}
                        </div>
                        <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.primary[600], fontWeight: 'medium' }}>
                          Code: {client.branchCode || '—'}
                        </div>
                        <div style={{ fontSize: '11px', color: theme.colors.light.text.secondary, maxWidth: '200px', whiteSpace: 'normal', lineHeight: '1.2' }}>
                          {client.address || '—'}
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell
                      style={{
                        color: canManageClients ? theme.colors.primary[600] : 'inherit',
                        fontWeight: theme.typography.fontWeight.bold
                      }}
                    >
                      <div
                        onClick={() => {
                          if (canManageClients) {
                            setSelectedClientForRequiredGuards(client);
                            setShowRequiredGuardsModal(true);
                          }
                        }}
                        style={{
                          cursor: canManageClients ? 'pointer' : 'default',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center'
                        }}
                      >
                        <span>{client.requiredGuard || 3}</span>
                        {client.otGuardCount && client.otGuardCount > 0 ? (
                          <span style={{ fontSize: '10px', color: theme.colors.accent.info, fontWeight: 'medium' }}>
                            +{client.otGuardCount} OT
                          </span>
                        ) : null}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      {renderGuards(client)}
                    </Table.Cell>
                    <Table.Cell>
                      <span style={{ fontWeight: theme.typography.fontWeight.bold, color: theme.colors.light.text.primary }}>
                        Rs. {client.salary?.toLocaleString() || '0'}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <StatusBadge variant={client.hasIssue ? 'incomplete' : 'complete'}>
                        {client.hasIssue ? 'Incomplete' : 'Deployed'}
                      </StatusBadge>
                    </Table.Cell>
                    <Table.Cell>
                      <div style={{ display: 'flex', gap: theme.spacing[2], alignItems: 'center' }}>
                        {client.deployedGuards && client.deployedGuards.length > 0 && (
                          <ActionButton
                            onClick={() => {
                              setSelectedClientForDocuments(client);
                              setShowDocumentsModal(true);
                            }}
                            title={client.allGuardsHaveDocuments ? "View Deployment Documents (PDF)" : "Attach Deployment Documents (PDF)"}
                            style={{
                              backgroundColor: !client.allGuardsHaveDocuments ? theme.colors.accent.warning : 'transparent',
                              color: !client.allGuardsHaveDocuments ? theme.colors.white : theme.colors.light.text.secondary,
                            }}
                          >
                            <FiFileText size={18} />
                          </ActionButton>
                        )}
                        <ActionButton
                          onClick={() => handleViewHistory(client)}
                          title="View History"
                        >
                          <FiEye size={18} />
                        </ActionButton>
                        <ActionButton
                          onClick={() => {
                            setSelectedClient(client);
                            const recipients = [];
                            if (client.psoEmail) recipients.push(client.psoEmail);
                            if (client.bomEmail) recipients.push(client.bomEmail);
                            setEmailRecipients(recipients);
                            setShowEmailModal(true);
                            // Load documents for attachments
                            loadAvailableDocuments(client);
                            setIsBodyLoaded(false);
                          }}
                          title="Send Email"
                          style={{ backgroundColor: theme.colors.primary[50], color: theme.colors.primary[600] }}
                        >
                          <FiMail size={18} />
                        </ActionButton>
                        <ActionButton
                          onClick={() => {
                            setSelectedClient(client);
                            setShowSalaryModal(true);
                          }}
                          title="Manage Salary"
                          style={{ backgroundColor: theme.colors.accent.info, color: theme.colors.white }}
                        >
                          <FiDollarSign size={18} />
                        </ActionButton>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            <EmptyState>
              <EmptyStateText>No clients found.</EmptyStateText>
            </EmptyState>
          )}

          {!loading && filteredClients.length > 0 && (
            <PaginationContainer>
              <PaginationInfo>
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredClients.length)} of {filteredClients.length} clients
              </PaginationInfo>
              <PaginationControls>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <PaginationInfo>
                  Page {currentPage} of {totalPages}
                </PaginationInfo>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </PaginationControls>
            </PaginationContainer>
          )}
        </CardBody>
      </Card>

      {/* History Modal */}
      <Modal
        isOpen={showHistoryModal}
        onClose={() => {
          setShowHistoryModal(false);
          setSelectedClient(null);
        }}
        title={`Deployment History - ${selectedClient?.bankName || 'Client'} ${selectedClient?.branchCode || ''} `}
        size="lg"
        footer={
          <Button variant="secondary" onClick={() => {
            setShowHistoryModal(false);
            setSelectedClient(null);
          }}>
            Close
          </Button>
        }
      >
        {selectedClient && (
          <div style={{ padding: theme.spacing[2] }}>
            {selectedClient.currentDeployment && (
              <DetailGroup>
                <DetailLabel style={{ marginBottom: theme.spacing[2], display: 'block' }}>Current Deployment</DetailLabel>
                <HistoryCard style={{ borderLeft: `4px solid ${theme.colors.accent.success} ` }}>
                  <HistoryCardHeader>
                    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
                      <FiUser color={theme.colors.primary[500]} />
                      <span style={{ fontWeight: theme.typography.fontWeight.bold, color: theme.colors.light.text.primary }}>
                        Active Guard
                      </span>
                    </div>
                    <Badge variant="active">Active</Badge>
                  </HistoryCardHeader>
                  <HistoryCardBody>
                    <GuardAvatar>
                      {selectedClient.currentDeployment.guard?.image ? (
                        <img src={selectedClient.currentDeployment.guard.image} alt="Guard" />
                      ) : (
                        <FiUser size={24} color={theme.colors.light.text.muted} />
                      )}
                    </GuardAvatar>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <div style={{ fontWeight: theme.typography.fontWeight.bold, fontSize: theme.typography.fontSize.base }}>
                        {selectedClient.currentDeployment.guard?.name || '—'}
                      </div>
                      <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
                        CNIC: {selectedClient.currentDeployment.guard?.cnic || '—'}
                      </div>
                      <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.primary[600] }}>
                        ID: {selectedClient.currentDeployment.guard?.guardId || '—'}
                      </div>
                    </div>
                  </HistoryCardBody>
                  <HistoryCardFooter>
                    <DateBadge>
                      <FiClock size={14} />
                      Deployed: {formatDeploymentDate(selectedClient.currentDeployment.postDate)}
                    </DateBadge>
                    {selectedClient.currentDeployment.slotNumber && (
                      <span style={{ fontWeight: theme.typography.fontWeight.medium }}>
                        Slot {selectedClient.currentDeployment.slotNumber}
                      </span>
                    )}
                  </HistoryCardFooter>
                </HistoryCard>
              </DetailGroup>
            )}

            {selectedClient.history && selectedClient.history.length > 0 && (
              <DetailGroup style={{ marginTop: theme.spacing[6] }}>
                <DetailLabel style={{ marginBottom: theme.spacing[2], display: 'block' }}>Previous Deployments</DetailLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[2] }}>
                  {selectedClient.history.map((deployment, index) => (
                    <HistoryCard key={index}>
                      <HistoryCardHeader>
                        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
                          <FiUser color={theme.colors.light.text.muted} />
                          <span style={{ fontWeight: theme.typography.fontWeight.medium, color: theme.colors.light.text.secondary }}>
                            Past Deployment
                          </span>
                        </div>
                        <Badge variant="completed">Completed</Badge>
                      </HistoryCardHeader>
                      <HistoryCardBody>
                        <GuardAvatar>
                          {deployment.guard?.image ? (
                            <img src={deployment.guard.image} alt="Guard" />
                          ) : (
                            <FiUser size={24} color={theme.colors.light.text.muted} />
                          )}
                        </GuardAvatar>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <div style={{ fontWeight: theme.typography.fontWeight.bold, fontSize: theme.typography.fontSize.base }}>
                            {deployment.guard?.name || deployment.guard?.guardId || '—'}
                          </div>
                          <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
                            CNIC: {deployment.guard?.cnic || '—'}
                          </div>
                        </div>
                      </HistoryCardBody>
                      <HistoryCardFooter>
                        <div style={{ display: 'flex', gap: theme.spacing[3] }}>
                          <DateBadge>
                            <FiClock size={14} />
                            From: {formatDeploymentDate(deployment.postDate)}
                          </DateBadge>
                          <DateBadge>
                            <FiX size={14} />
                            To: {formatDeploymentDate(deployment.endDate)}
                          </DateBadge>
                        </div>
                        {deployment.slotNumber && (
                          <span style={{ fontWeight: theme.typography.fontWeight.medium }}>
                            Slot {deployment.slotNumber}
                          </span>
                        )}
                      </HistoryCardFooter>
                    </HistoryCard>
                  ))}
                </div>
              </DetailGroup>
            )}

            {(!selectedClient.history || selectedClient.history.length === 0) && !selectedClient.currentDeployment && (
              <EmptyState>
                <FiUser size={48} style={{ opacity: 0.2, marginBottom: theme.spacing[3] }} />
                <EmptyStateText>No deployment history available.</EmptyStateText>
              </EmptyState>
            )}
          </div>
        )}
      </Modal>

      <DeploymentModal
        isOpen={showDeploymentModal}
        onClose={() => {
          setShowDeploymentModal(false);
          setInitialDeploymentData(null);
        }}
        clients={deploymentClients}
        clientType="Bank"
        onDeploymentSaved={() => {
          loadData();
          setShowDeploymentModal(false);
          setInitialDeploymentData(null);
        }}
        initialClientPath={initialDeploymentData?.path}
        initialSlot={initialDeploymentData?.slot}
        initialDate={initialDeploymentData?.date}
      />

      <GuardTerminationModal
        isOpen={showTerminationModal}
        onClose={() => setShowTerminationModal(false)}
        guard={terminationGuard}
        clientPath={terminationClientPath}
        companyId={companyId as string}
        onTerminated={handleTerminated}
      />

      <RequiredGuardsModal
        isOpen={showRequiredGuardsModal}
        onClose={() => setShowRequiredGuardsModal(false)}
        client={selectedClientForRequiredGuards}
        onUpdate={handleUpdateRequiredGuards}
        loading={updatingRequiredGuards}
      />

      {selectedClientForDocuments && (
        <GuardDocumentsModal
          isOpen={showDocumentsModal}
          onClose={() => {
            setShowDocumentsModal(false);
            setSelectedClientForDocuments(null);
          }}
          clientPath={`bankClient/${companyId}/banks/${selectedClientForDocuments.bankId}/branches/${selectedClientForDocuments.branchId}`}
          clientName={`${selectedClientForDocuments.bankName || ''} ${selectedClientForDocuments.branchCode || ''} `.trim() || 'Client'}
          onDocumentsUpdated={() => {
            loadData();
          }}
        />
      )}

      {/* Email Modal */}
      <Modal
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          setIsBodyLoaded(false);
          setSelectedAttachments([]);
        }}
        title={`Send Deployment Details - ${selectedClient?.bankName} (${selectedClient?.branchCode})`}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowEmailModal(false)} disabled={sendingEmail}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                if (emailRecipients.length === 0) {
                  toast.error('At least one recipient is required');
                  return;
                }
                setSendingEmail(true);
                try {
                  const attachments = selectedAttachments.map(url => {
                    const doc = availableDocuments.find(d => d.url === url);
                    return {
                      filename: doc?.name || 'document.pdf',
                      path: url
                    };
                  });

                  const message: any = {
                    subject: `Deployment Update: ${selectedClient?.bankName} - ${selectedClient?.branchCode} `,
                    html: emailBody.replace(/\n/g, '<br/>'),
                  };

                  if (attachments.length > 0) {
                    message.attachments = attachments;
                  }

                  await sendEmailViaFirestore({
                    to: emailRecipients,
                    message: message,
                    companyName: companyData?.name
                  });
                  toast.success('Email sent successfully');
                  setShowEmailModal(false);
                } catch (err: any) {
                  toast.error(err.message);
                } finally {
                  setSendingEmail(false);
                }
              }}
              disabled={sendingEmail}
              loading={sendingEmail}
            >
              Send Email
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
          {/* Recipients */}
          <div>
            <DetailLabel style={{ marginBottom: theme.spacing[2], display: 'block' }}>Recipients</DetailLabel>
            <ChipContainer>
              {emailRecipients.map((email, idx) => (
                <Chip key={idx}>
                  {email}
                  <FiX
                    size={14}
                    onClick={() => setEmailRecipients(prev => prev.filter((_, i) => i !== idx))}
                  />
                </Chip>
              ))}
              <input
                type="text"
                value={newRecipient}
                onChange={(e) => setNewRecipient(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newRecipient.trim()) {
                    if (!newRecipient.includes('@')) {
                      toast.error('Invalid email');
                      return;
                    }
                    setEmailRecipients([...emailRecipients, newRecipient.trim()]);
                    setNewRecipient('');
                  }
                }}
                placeholder="Add email and press Enter..."
                style={{
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  padding: '4px',
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.light.text.primary,
                  flex: 1,
                  minWidth: '150px'
                }}
              />
            </ChipContainer>
          </div>

          {/* Attachments */}
          <div>
            <DetailLabel style={{ marginBottom: theme.spacing[2], display: 'block' }}>Attach Documents</DetailLabel>
            {availableDocuments.length > 0 ? (
              <AttachmentList>
                {availableDocuments.map((doc, idx) => (
                  <AttachmentItem
                    key={idx}
                    active={selectedAttachments.includes(doc.url)}
                    onClick={() => {
                      setSelectedAttachments(prev =>
                        prev.includes(doc.url)
                          ? prev.filter(u => u !== doc.url)
                          : [...prev, doc.url]
                      );
                      setIsBodyLoaded(false); // Trigger body rebuild
                    }}
                  >
                    <div style={{ color: theme.colors.primary[500] }}>
                      <FiFileText size={24} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: theme.typography.fontSize.xs, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {doc.name}
                      </div>
                      <div style={{ fontSize: '10px', color: theme.colors.light.text.secondary }}>
                        {doc.guardName}
                      </div>
                    </div>
                  </AttachmentItem>
                ))}
              </AttachmentList>
            ) : (
              <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.muted, fontStyle: 'italic' }}>
                No documents found in current deployment.
              </div>
            )}
          </div>

          {/* Email Body */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing[2] }}>
              <DetailLabel>Email Content</DetailLabel>
              {!isBodyLoaded && selectedClient && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => generateEmailBody(selectedClient)}
                >
                  Regenerate Template
                </Button>
              )}
            </div>
            <EmailBody
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              placeholder="Write your professional email here..."
            />
          </div>
        </div>
      </Modal>

      {/* Helper functions for Email Modal */}
      {(() => {
        // This is a trick to include functions in the component scope
        // but defined after the main return for readability
        if (showEmailModal && !isBodyLoaded && selectedClient) {
          generateEmailBody(selectedClient);
        }
        return null;
      })()}

      {/* Salary Update Modal */}
      <Modal
        isOpen={showSalaryModal}
        onClose={() => {
          setShowSalaryModal(false);
          setSelectedClient(null);
        }}
        title={`Salary Management - ${selectedClient?.bankName || ''} (${selectedClient?.branchCode || ''})`}
        footer={
          <Button variant="secondary" onClick={() => setShowSalaryModal(false)}>
            Close
          </Button>
        }
      >
        <div style={{ padding: theme.spacing[4] }}>
          <div style={{ marginBottom: theme.spacing[6], padding: theme.spacing[4], background: theme.colors.light.background.secondary, borderRadius: theme.borderRadius.lg }}>
            <h3 style={{ margin: `0 0 ${theme.spacing[3]} 0`, fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>Set New Salary</h3>
            <div style={{ display: 'flex', gap: theme.spacing[2] }}>
              <div style={{ flex: 1 }}>
                <Input
                  type="number"
                  placeholder="Enter new salary"
                  value={selectedClient?.salary?.toString() || '0'}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    setSelectedClient(prev => prev ? { ...prev, salary: val } : null);
                  }}
                  dark
                  fullWidth
                />
              </div>
              <Button
                variant="primary"
                onClick={async () => {
                  if (!selectedClient) return;
                  try {
                    setLoading(true);
                    await updateBankClient(
                      selectedClient.branchId,
                      selectedClient.bankId,
                      { salary: selectedClient.salary },
                      companyId
                    );
                    toast.success('Salary updated successfully');
                    await loadData();
                    setShowSalaryModal(false);
                  } catch (error) {
                    toast.error('Failed to update salary');
                  } finally {
                    setLoading(false);
                  }
                }}
                loading={loading}
                disabled={loading}
              >
                Update
              </Button>
            </div>
          </div>

          <h3 style={{ margin: `0 0 ${theme.spacing[3]} 0`, fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>Salary History</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[3], maxHeight: '300px', overflowY: 'auto' }}>
            {selectedClient?.salaryHistory && selectedClient.salaryHistory.length > 0 ? (
              [...selectedClient.salaryHistory].reverse().map((entry, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: theme.spacing[3],
                  background: theme.colors.light.background.tertiary,
                  borderLeft: `4px solid ${entry.type === 'increase' ? theme.colors.accent.success : entry.type === 'decrease' ? theme.colors.accent.danger : theme.colors.primary[500]}`,
                  borderRadius: theme.borderRadius.md
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 'bold', color: theme.colors.light.text.primary }}>Rs. {entry.amount.toLocaleString()}</span>
                    <span style={{ fontSize: '10px', color: theme.colors.light.text.muted }}>
                      {entry.date?.toDate ? entry.date.toDate().toLocaleDateString() : new Date(entry.date).toLocaleDateString()}
                    </span>
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: 'bold', color: entry.type === 'increase' ? theme.colors.accent.success : entry.type === 'decrease' ? theme.colors.accent.danger : theme.colors.primary[500] }}>
                    {entry.type === 'set' ? 'INITIAL' : entry.type === 'increase' ? 'INCREASE' : 'DECREASE'}
                  </span>
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: theme.spacing[4], color: theme.colors.light.text.muted }}>
                No salary history available.
              </div>
            )}
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default BankDeploymentPage;
